import { Skeleton } from '@pay-com/components'
import { DownloadButton } from 'components/DownloadButton'
import { FilePreviewIcon } from 'components/FilePreviewIcon'
import { useGetDocumentsByIdsQuery } from 'generated/graphql'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'

type TaskAttachmentsFieldPreviewProps = {
  ids: string[]
  fieldLabel?: string
  className?: string
}

const MAX_FILENAME_LENGTH = 40

const trimFilename = (filename?: string | null): string => {
  if (!filename) return ''
  if (filename.length <= MAX_FILENAME_LENGTH) return filename

  const ext = filename.lastIndexOf('.')
  if (ext === -1) return `${filename.slice(0, MAX_FILENAME_LENGTH)}...`

  const name = filename.slice(0, ext)
  const extension = filename.slice(ext)
  const maxNameLength = MAX_FILENAME_LENGTH - extension.length - 3
  return `${name.slice(0, maxNameLength)}...${extension}`
}

export const TaskAttachmentsFieldPreview = ({
  ids,
  fieldLabel,
  className
}: TaskAttachmentsFieldPreviewProps) => {
  const { t } = useTranslation()
  const { data, loading, error } = useGetDocumentsByIdsQuery({
    variables: {
      input: {
        documentsIds: ids.map(id => ({ id }))
      }
    },
    skip: !ids.length
  })

  if (loading) {
    return (
      <div className="flex flex-col gap-2">
        {ids?.map(id => (
          <div key={id}>
            <Skeleton className="h-8 w-80" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={twMerge('flex flex-col gap-0.5', className)}>
      {fieldLabel ? (
        <span className="text-sm font-medium text-blueGray-900">
          {fieldLabel}
        </span>
      ) : null}
      <div className="flex flex-col gap-2">
        {!ids?.length && <span>{t('tasks.noDocuments')}</span>}
        {error && (
          <span className="text-red-500">
            {t('tasks.errorLoadingDocuments')}
          </span>
        )}
        {data?.documentsByIds?.documents?.map(
          ({ contentType, id, originalFilename, status }) => (
            <div className="flex items-center" key={id}>
              <div className="flex items-center gap-2">
                <FilePreviewIcon
                  className="size-6 text-blueGray-500"
                  fill="currentColor"
                  contentType={contentType}
                />
                <div className="text-sm">{trimFilename(originalFilename)}</div>
              </div>
              <DownloadButton
                id={id}
                uploadStatus={status}
                disableButtonStyles
              />
            </div>
          )
        )}
      </div>
    </div>
  )
}
